// Чертёж настенного крепежа: вид сбоку и вид планки.
// Размеры в мм совпадают с листами YKRS / YKWK.

const SPECS = {
  'YKRS-01': { top: 90, bottom: 90, brace: false, single: false, armZ: 10 },
  'YKRS-02': { top: 80, bottom: 160, brace: true, single: false, armZ: 20 },
  'YKWK-01': { top: 170, bottom: 90, brace: false, single: true, armZ: 10 },
  'YKWK-02': { top: 160, bottom: 140, brace: true, single: true, armZ: 20 },
};

export function isWallFastenerKey(key) {
  return !!SPECS[key];
}

export function isFastenerDrawingKey(key) {
  return isWallFastenerKey(key)
    || key === 'KRNS-GS-03'
    || key === 'KRNS-GS-04'
    || key === 'Крепеж напольный нерегулируемый';
}

function fmt(n) {
  const r = Math.round(Number(n) * 10) / 10;
  if (!Number.isFinite(r)) return '';
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

function arrowLines(x1, y1, x2, y2) {
  const ang = Math.atan2(y2 - y1, x2 - x1);
  const len = 8;
  const spread = 0.42;
  const tip = (sign) => {
    const a = ang + Math.PI + sign * spread;
    return [x2 + len * Math.cos(a), y2 + len * Math.sin(a)];
  };
  const a = tip(-1);
  const b = tip(1);
  return [
    [x1, y1, x2, y2],
    [x2, y2, a[0], a[1]],
    [x2, y2, b[0], b[1]],
  ];
}

export function buildWallFastenerDrawing({ key, clampDiameter, axialDistance, overhang }) {
  const spec = SPECS[key];
  if (!spec) return null;

  const D = Number(clampDiameter) > 0 ? Number(clampDiameter) : 88.9;
  const V = Number(overhang) > 0 ? Number(overhang) : 150;
  const axial = spec.single ? 0 : (Number(axialDistance) > 0 ? Number(axialDistance) : 300);
  const barH = spec.bottom + axial + spec.top;
  const armH = spec.armZ * 2;
  const R = D / 2;
  const neck = 8;
  const face = V - 20;
  const arms = spec.single ? [spec.bottom] : [spec.bottom, spec.bottom + axial];

  const circleZ = (center) => center + spec.armZ + neck + R;
  const zMax = Math.max(barH, ...arms.map(circleZ));
  const xMin = -Math.max(22, R + 12);
  const xMax = V;

  const margin = { l: 96, t: 36, r: 64, b: 86 };
  const gap = 130;
  const frontPad = 92;
  const targetW = 920;
  const targetH = 560;
  const geomW = xMax - xMin;
  const geomH = zMax;
  const s = Math.max(0.35, Math.min(
    (targetW - margin.l - margin.r - gap - frontPad) / geomW,
    (targetH - margin.t - margin.b) / geomH,
    1.35
  ));

  const width = Math.ceil(margin.l + geomW * s + gap + 40 * s + frontPad + margin.r);
  const height = Math.ceil(margin.t + geomH * s + margin.b);

  const X = (x) => margin.l + (x - xMin) * s;
  const Y = (z) => margin.t + (zMax - z) * s;
  const frontX = X(xMax) + gap;

  const shapes = [];
  const line = (x1, y1, x2, y2, sw = 1.6, dash) => {
    shapes.push({ type: 'line', x1, y1, x2, y2, sw, dash });
  };
  const rect = (x, z, w, h) => {
    shapes.push({
      type: 'rect',
      x: X(x),
      y: Y(z + h),
      w: w * s,
      h: h * s,
    });
  };

  rect(face, 0, 20, barH);

  arms.forEach((c) => {
    rect(-20, c - spec.armZ, face + 20, armH);
    rect(-4, c + spec.armZ, 8, neck);
    const cz = circleZ(c);
    shapes.push({ type: 'circle', cx: X(0), cy: Y(cz), r: R * s, sw: 1.6 });
    line(X(-R - 11), Y(cz), X(-R), Y(cz), 1.4);
    line(X(R), Y(cz), X(R + 11), Y(cz), 1.4);
    line(X(0), Y(cz - R), X(0), Y(cz + R), 0.6, '4 3');
    line(X(-R), Y(cz), X(R), Y(cz), 0.6, '4 3');
    if (spec.brace) {
      const yb = c - spec.armZ;
      shapes.push({
        type: 'path',
        d: `M ${X(face)} ${Y(yb)} L ${X(face)} ${Y(yb - 80)} L ${X(face - 80)} ${Y(yb)} Z`,
      });
    }
  });

  const holes = [20, barH - 20];
  if (!spec.single) holes.splice(1, 0, barH / 2);
  const barFrontX = frontX;
  shapes.push({
    type: 'rect',
    x: barFrontX,
    y: Y(barH),
    w: 40 * s,
    h: barH * s,
  });
  line(barFrontX + 20 * s, Y(0), barFrontX + 20 * s, Y(barH), 0.6, '4 3');
  holes.forEach((z) => {
    shapes.push({
      type: 'circle',
      cx: barFrontX + 20 * s,
      cy: Y(z),
      r: 5 * s,
      sw: 1.4,
    });
  });

  const dimSw = 0.8;
  const pushDimLines = (x1, y1, x2, y2) => {
    arrowLines(x1, y1, x2, y2).forEach(([a, b, c, d]) => line(a, b, c, d, dimSw));
  };

  const hDim = (x1, x2, y, text, textDy = -5) => {
    const span = Math.abs(x2 - x1);
    const tw = Math.max(16, text.length * 7.2);
    if (span < tw + 18) {
      const left = Math.min(x1, x2);
      const right = Math.max(x1, x2);
      pushDimLines(left, y, right, y);
      shapes.push({
        type: 'text',
        x: right + 4,
        y: y + 4,
        text,
        anchor: 'start',
      });
      return;
    }
    const mid = (x1 + x2) / 2;
    const gap = tw / 2 + 3;
    const dir = x2 >= x1 ? 1 : -1;
    pushDimLines(x1, y, mid - dir * gap, y);
    pushDimLines(x2, y, mid + dir * gap, y);
    shapes.push({ type: 'text', x: mid, y: y + textDy, text, anchor: 'middle' });
  };

  const vDim = (z1, z2, x, text) => {
    const y1 = Y(z1);
    const y2 = Y(z2);
    const span = Math.abs(y2 - y1);
    const tw = Math.max(16, text.length * 7.2);
    if (span < tw + 18) {
      const top = Math.min(y1, y2);
      pushDimLines(x, y1, x, y2);
      shapes.push({ type: 'text', x: x + 4, y: top - 3, text, anchor: 'start' });
      return;
    }
    const mid = (y1 + y2) / 2;
    const gap = tw / 2 + 3;
    const dir = y2 >= y1 ? 1 : -1;
    pushDimLines(x, y1, x, mid - dir * gap);
    pushDimLines(x, y2, x, mid + dir * gap);
    shapes.push({
      type: 'text',
      x,
      y: mid,
      text,
      anchor: 'middle',
      rotate: -90,
    });
  };

  const ext = (x1, y1, x2, y2) => line(x1, y1, x2, y2, 0.6);

  const yFace = Y(0) + 28;
  const yAll = Y(0) + 52;
  ext(X(0), Y(0) + 4, X(0), yAll + 4);
  ext(X(face), Y(0) + 4, X(face), yFace + 4);
  ext(X(V), Y(0) + 4, X(V), yAll + 4);
  hDim(X(0), X(face), yFace, fmt(face));
  hDim(X(0), X(V), yAll, fmt(V));
  hDim(X(face), X(V), yFace, '20');

  const xVert = X(V) + 36;
  const levels = [barH, ...[...arms].reverse(), 0];
  levels.forEach((z) => ext(X(V) + 4, Y(z), xVert + 4, Y(z)));
  for (let i = 0; i < levels.length - 1; i += 1) {
    const a = levels[i];
    const b = levels[i + 1];
    vDim(a, b, xVert, fmt(a - b));
  }

  if (spec.brace) {
    const c = arms[0];
    const yb = c - spec.armZ;
    hDim(X(face - 80), X(face), Y(yb) + 16, '80', 14);
    vDim(yb - 80, yb, X(face) - 14, '80');
  }

  if (armH >= 30) {
    const c = arms[0];
    const armDimX = Math.min(R + 18, face - 28);
    ext(X(armDimX) - 10, Y(c - spec.armZ), X(armDimX) + 4, Y(c - spec.armZ));
    ext(X(armDimX) - 10, Y(c + spec.armZ), X(armDimX) + 4, Y(c + spec.armZ));
    vDim(c - spec.armZ, c + spec.armZ, X(armDimX), fmt(armH));
  }

  const topArm = arms[arms.length - 1];
  const cz = circleZ(topArm);
  shapes.push({
    type: 'text',
    x: X(-R) - 8,
    y: Y(cz) - 6,
    text: `Ø${fmt(D)}`,
    anchor: 'end',
  });
  line(X(-R) - 6, Y(cz) - 2, X(-R * 0.45), Y(cz + R * 0.35), 0.7);

  const xPitch = barFrontX - 28;
  const chain = [barH, ...[...holes].reverse(), 0];
  chain.forEach((z) => ext(barFrontX - 3, Y(z), xPitch - 4, Y(z)));
  const pitchLabels = [fmt(20)];
  for (let i = 0; i < holes.length - 1; i += 1) {
    pitchLabels.push(fmt(holes[i + 1] - holes[i]));
  }
  pitchLabels.push(fmt(20));
  const pitchZs = [barH, ...[...holes].reverse(), 0];
  for (let i = 0; i < pitchZs.length - 1; i += 1) {
    vDim(pitchZs[i], pitchZs[i + 1], xPitch, pitchLabels[i]);
  }

  const xHeight = barFrontX + 40 * s + 28;
  ext(barFrontX + 40 * s + 3, Y(barH), xHeight + 4, Y(barH));
  ext(barFrontX + 40 * s + 3, Y(0), xHeight + 4, Y(0));
  vDim(barH, 0, xHeight, fmt(barH));

  const yWidth = Y(0) + 28;
  ext(barFrontX, Y(0) + 3, barFrontX, yWidth + 4);
  ext(barFrontX + 40 * s, Y(0) + 3, barFrontX + 40 * s, yWidth + 4);
  hDim(barFrontX, barFrontX + 40 * s, yWidth, '40');

  const holeLabelZ = holes[0];
  shapes.push({
    type: 'text',
    x: barFrontX + 20 * s + 5 * s + 10,
    y: Y(holeLabelZ) + 4,
    text: 'Ø10',
    anchor: 'start',
  });

  return {
    width,
    height,
    shapes,
    barH,
    face,
    overhang: V,
    diameter: D,
    axial: spec.single ? null : axial,
  };
}

export function fastenerDrawingSvg(model) {
  if (!model) return '';
  const parts = model.shapes.map((shape) => {
    if (shape.type === 'line') {
      const dash = shape.dash ? ` stroke-dasharray="${shape.dash}"` : '';
      return `<line x1="${shape.x1}" y1="${shape.y1}" x2="${shape.x2}" y2="${shape.y2}" stroke="#111" stroke-width="${shape.sw}" fill="none"${dash}/>`;
    }
    if (shape.type === 'rect') {
      return `<rect x="${shape.x}" y="${shape.y}" width="${shape.w}" height="${shape.h}" stroke="#111" stroke-width="1.6" fill="#fff"/>`;
    }
    if (shape.type === 'circle') {
      return `<circle cx="${shape.cx}" cy="${shape.cy}" r="${shape.r}" stroke="#111" stroke-width="${shape.sw}" fill="#fff"/>`;
    }
    if (shape.type === 'path') {
      return `<path d="${shape.d}" stroke="#111" stroke-width="1.6" fill="#fff"/>`;
    }
    if (shape.type === 'text') {
      const rot = shape.rotate ? ` transform="rotate(${shape.rotate} ${shape.x} ${shape.y})"` : '';
      return `<text x="${shape.x}" y="${shape.y}" text-anchor="${shape.anchor || 'middle'}" font-family="Arial, sans-serif" font-size="13" fill="#111"${rot}>${shape.text}</text>`;
    }
    return '';
  });
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${model.width}" height="${model.height}" viewBox="0 0 ${model.width} ${model.height}">${parts.join('')}</svg>`;
}
